/**
 * Core organization service functions.
 * Member/invitation ops -> ./org-members.ts
 * Subscription/credit-tier ops -> ./subscriptions.ts
 */
import { apiClient } from '@/lib/api-client';

export async function fetchOrganization(_orgId: string) {
  return apiClient.get('/organizations/me');
}

export async function updateOrganizationDb(orgId: string, updates: Record<string, unknown>) {
  await apiClient.patch('/organizations/' + orgId, updates);
}

/**
 * Create the caller's organisation.
 *
 * ─── No `slug`, and no `id` ──────────────────────────────────────────────────
 *
 * Both used to be minted here and both were wrong to send.
 *
 * `id` was stripped by the API's validation whitelist and the server generated
 * its own, so the UUID this function invented named nothing and was handed back
 * to the caller as though it were the new organisation.
 *
 * `slug` was the damaging one. It was built with a fourth hand-rolled copy of
 * the slug rules, `[^a-z0-9]+` to dashes, which deletes every character of a
 * Greek or a Russian name. `Ακρόπολις` collapsed to an empty string and the
 * timestamp was appended to nothing, giving `-mt6d6j6q`, leading dash included.
 * That value seeds the review page's slug, which is the public review funnel
 * URL a customer prints on a card. Reproduced end to end in the browser on a
 * fresh sign-up. Greek is the home market, so this was the default outcome
 * rather than an edge case.
 *
 * The server already derives the slug with `shared/slug.ts`, which transcribes
 * Greek by ELOT 743 Type 2 and Russian by BGN/PCGN and gives `akropolis`. Not
 * sending one is what lets that code run: `CreateOrganizationSelfDto.slug` is
 * optional precisely so the server can be the single implementation.
 */
export async function insertOrganization(org: {
  name: string;
  ownerId: string;
  billingEmail?: string;
  defaultSenderId?: string | null;
  timezone?: string | null;
}) {
  return apiClient.post<{ id: string }>('/organizations', org);
}

/** One row of `GET /organizations/memberships`. */
export interface OrganizationMembership {
  membershipId: string;
  organizationId: string;
  organizationName: string;
  organizationActive: boolean;
  role: string;
  /** True for the one the session's access token is minted from. */
  isPrimary: boolean;
}

/**
 * Every organisation this account holds a seat in.
 *
 * The only read that steps outside the caller's current organisation, and one
 * of the few that still answers when that organisation is deactivated. Normally
 * one row: an account may belong to exactly one organisation, and more than one
 * row means an account that predates the rule being enforced.
 */
export async function fetchMyMemberships() {
  return apiClient.get<OrganizationMembership[]>('/organizations/memberships');
}

/** What the server reports after a membership is deleted. */
export interface LeaveOrganizationResult {
  left: boolean;
  organizationId: string;
  organizationName: string;
  /** Always true: the access token still names the organisation just left. */
  signOutRequired: boolean;
}

/**
 * Give up your seat in an organisation.
 *
 * Refused for the owner, whose membership row is the only thing making the
 * organisation owned at all. Callers must sign out afterwards: the access token
 * carries the organisation id as a claim and is not rewritten by this, so every
 * request made with the old one resolves a membership that no longer exists.
 */
export async function leaveOrganization(organizationId: string) {
  return apiClient.post<LeaveOrganizationResult>('/organizations/leave', { organizationId });
}
